import { FC, useState } from 'react'
import type { CSSProperties } from 'react'
import { TextInput } from '@/components/Generic'
import type { EditableField, EditableFieldValues } from '@/components/SignDocumentCommon/types'
import { isIbanField, isNumberField, isValidIban } from '@/methods/validateEditableFields'
import { cx } from '@/utils/cx'
import './EditableFieldsOverlay.css'

const FIELD_WIDTH_PT = 200
const FIELD_HEIGHT_PT = 18

export interface EditableFieldsConfig {
  fields: EditableField[]
  values: EditableFieldValues
  onChange: (key: string, value: string) => void
  requiredErrorLabel?: string
  ibanErrorLabel?: string
  onFieldsResolved?: (keys: string[]) => void
}

interface EditableFieldsOverlayProps extends EditableFieldsConfig {
  pageNumber: number
  pageWidthPt: number
  pageHeightPt: number
}

export const EditableFieldsOverlay: FC<EditableFieldsOverlayProps> = ({
  fields,
  pageNumber,
  pageWidthPt,
  pageHeightPt,
  values,
  onChange,
  requiredErrorLabel,
  ibanErrorLabel,
}) => {
  const [touchedKeys, setTouchedKeys] = useState<string[]>([])
  const markTouched = (key: string) =>
    setTouchedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]))

  const pageFields = fields.filter(
    (field) =>
      field.location.page === pageNumber &&
      field.location.top !== undefined &&
      field.location.left !== undefined
  )

  if (pageFields.length === 0 || !pageWidthPt || !pageHeightPt) return null

  const rows = pageFields.map((field) => {
    const value = values[field.key] ?? ''
    const touched = touchedKeys.includes(field.key)

    let errorLabel: string | undefined
    if (touched) {
      if (!value.trim()) {
        errorLabel = requiredErrorLabel
      } else if (isIbanField(field) && !isValidIban(value)) {
        errorLabel = ibanErrorLabel
      }
    }

    const style = {
      '--ef-left': `${((field.location.left ?? 0) / pageWidthPt) * 100}%`,
      '--ef-top': `${((field.location.top ?? 0) / pageHeightPt) * 100}%`,
      '--ef-width': `${(FIELD_WIDTH_PT / pageWidthPt) * 100}%`,
      '--ef-height': `${(FIELD_HEIGHT_PT / pageHeightPt) * 100}%`,
    } as CSSProperties

    return { field, value, errorLabel, style }
  })

  return (
    <div className="editable-fields-layer">
      {rows.map(({ field, value, errorLabel, style }) => (
        <div
          key={field.key}
          className={cx('editable-fields-field', errorLabel && 'editable-fields-field--error')}
          style={style}
        >
          <TextInput
            type="text"
            inputMode={isNumberField(field) ? 'numeric' : 'text'}
            value={value}
            onChange={(next: string) =>
              onChange(field.key, isNumberField(field) ? next.replace(/\D/g, '') : next)
            }
            onBlur={() => markTouched(field.key)}
            placeholder={field.label}
            ariaLabel={field.label}
            name={`editable-field-${field.key}`}
          />
        </div>
      ))}

      {rows.map(({ field, errorLabel, style }) =>
        errorLabel ? (
          <div
            key={`error-${field.key}`}
            className="editable-fields-error"
            style={style}
            role="alert"
          >
            {errorLabel}
          </div>
        ) : null
      )}
    </div>
  )
}
